import moment from 'src/app/utils/moment-timezone';
import { ByWeekday } from 'rrule';
import { DateTime } from 'luxon';
import { generateRecurringOccurrences, RecurrenceConfig } from '../utils/recurrence.utils';

export class  CalendarAvailability {
  lastModifiedDate?: string;
  lastModifiedBy?: string;
  id: string;
  start: string;
  creationDate: string;
  createdBy: string;
  timezone: string;
  isRecurring: boolean;
  duration?: number;
  durationQuantity?: number;
  durationUnit?: "minutes" | "hours";
  recurring?: {
    frequency: "daily" | "weekly" | "monthly";
    interval: number;
    until?: string;
    byDay?: ByWeekday[];
    byMonthDay?: number[];
    exclusions?: {
      [exclusionId: string]: {
        timestamp: string;
      }
    }
  };
  parent?: {
    id: string,
    occurrence: string,
  };

  constructor (init: Partial<CalendarAvailability>){
      Object.assign(this, init);
  }

  asEvent(): any {
    // Return events in UTC ISO format - FullCalendar will convert to display timezone
    return {
        id: this.id,
        start: (moment as any)(this.start).utc().toISOString(), // Always UTC
        end: (moment as any)(this.start).utc().add(this.duration, "minutes").toISOString(), // Always UTC
        //display: "background",
        duration: this.duration,
        color: "#2CB504",
        type: "availability",
        isRecurring: this.isRecurring,
    };  
  }


    getOccurrences(): any[] {
      if(!this.isRecurring || !this.recurring) {
        return []
      }

      // Use the availability's stored timezone, or guess if not available
      const timezone = this.timezone || moment.tz.guess();

      // Convert ByWeekday array to string array if needed
      // Our models store weekdays as strings like "TU", "TH", so this should be safe
      const byDayStrings: ("MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU")[] | undefined = 
        this.recurring.byDay ? 
          this.recurring.byDay.map(day => {
            if (typeof day === 'string') {
              return day as any;
            } else if (typeof day === 'number') {
              // Convert number to string (0=Monday, 1=Tuesday, etc.)
              const weekdayMap = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
              return weekdayMap[day] || "MO" as any;
            }
            return "MO" as any;
          }).filter((day): day is "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU" => 
            ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].includes(day as string)
          )
        : undefined;

      // Build recurrence config
      // Calculate in availability's timezone to get correct days/times
      // But return in UTC - calendar components will convert to viewing user's timezone
      const config: RecurrenceConfig = {
        start: this.start, // Already in UTC ISO format
        timezone: timezone, // Availability's timezone (where the trainer set the times) - used for calculation
        duration: this.duration || 60,
        frequency: this.recurring.frequency,
        interval: this.recurring.interval || 1,
        until: this.recurring.until,
        byDay: byDayStrings,
        byMonthDay: this.recurring.byMonthDay,
        exclusions: this.recurring.exclusions,
        returnInLocalTimezone: false // Return in UTC - calendar will convert to viewing user's timezone
      };

      // Generate occurrences using our custom utility
      const occurrences = generateRecurringOccurrences(config);

      // Convert to event format expected by the calendar
      return occurrences.map(occurrence => {
        const event = this.asEvent();
        event.start = occurrence.start;
        event.end = occurrence.end;
        event.startOriginal = this.start;
        event.startUTC = occurrence.start;
        return event;
      });
    }
  


  getRecurringExlusions(): string[] {
    if(this.recurring && this.recurring.exclusions && Object.keys(this.recurring.exclusions).length > 0) {
      const exclusions = this.recurring.exclusions;
      const toReturn = [];
      for (const id in exclusions) {
        if (exclusions.hasOwnProperty(id)) {
            const exclusionObj  = exclusions[id];
            toReturn.push(exclusionObj.timestamp);
        }
    }
      return toReturn
    } else {
      return null
    }
  }

  getEndTime(): string {
    return this.start? (moment as any)(this.start).add(this.duration).toISOString() : null
  }
}